/*
 * RivanyOS Pixel — public/pixel.js
 *
 * Standalone, zero-dependency visitor-journey tracker for the May Anthony
 * Shopify storefronts (mayanthony.com / officialmay.myshopify.com = INT).
 * Embedded via theme.liquid — see docs/pixel-installation.md.
 *
 * Captures page views, product views, add-to-cart and purchase, with UTM /
 * fbclid / ad_id / referrer per touchpoint, and POSTs them to the RivanyOS
 * collector. It NEVER throws and NEVER blocks render — every external touch is
 * wrapped in try/catch and the work is deferred to idle time.
 */
(function () {
  'use strict'

  var ENDPOINT = 'https://rivanyos.vercel.app/api/pixel-event'

  // ── tiny storage + uuid helpers (all guarded) ────────────────────────────────
  function uuid() {
    try {
      if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0
      var v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
  function lsGet(k) { try { return window.localStorage.getItem(k) } catch (e) { return null } }
  function lsSet(k, v) { try { window.localStorage.setItem(k, v) } catch (e) {} }
  function ssGet(k) { try { return window.sessionStorage.getItem(k) } catch (e) { return null } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v) } catch (e) {} }

  // ── identity ──────────────────────────────────────────────────────────────────
  // visitor_id: persists across sessions (localStorage). session_id: per tab/session.
  function visitorId() {
    var id = lsGet('rv_visitor_id')
    if (!id) { id = uuid(); lsSet('rv_visitor_id', id) }
    return id
  }
  function sessionId() {
    var id = ssGet('rv_session_id')
    if (!id) { id = uuid(); ssSet('rv_session_id', id) }
    return id
  }

  // ── URL params ────────────────────────────────────────────────────────────────
  function params() {
    try { return new URLSearchParams(window.location.search) } catch (e) { return new URLSearchParams('') }
  }

  // UTMs are captured on the landing hit and held for the whole session, so a
  // visitor who navigates away from the landing URL still carries attribution.
  function utms() {
    var stored = ssGet('rv_utms')
    if (stored) { try { return JSON.parse(stored) } catch (e) {} }
    var p = params()
    var u = {
      utm_source: p.get('utm_source'),
      utm_medium: p.get('utm_medium'),
      utm_campaign: p.get('utm_campaign'),
      utm_content: p.get('utm_content'),
      utm_term: p.get('utm_term')
    }
    if (u.utm_source || u.utm_medium || u.utm_campaign || u.utm_content || u.utm_term) {
      ssSet('rv_utms', JSON.stringify(u))
    }
    return u
  }

  // fbclid / ad_id / campaign_id — same session-persistence treatment.
  function fbData() {
    var stored = ssGet('rv_fb')
    if (stored) { try { return JSON.parse(stored) } catch (e) {} }
    var p = params()
    var f = {
      fbclid: p.get('fbclid'),
      ad_id: p.get('ad_id'),
      campaign_id: p.get('campaign_id')
    }
    if (f.fbclid || f.ad_id || f.campaign_id) ssSet('rv_fb', JSON.stringify(f))
    return f
  }

  // ── store detection ─────────────────────────────────────────────────────────
  // officialmay.myshopify.com and mayanthony.com are the INT store. A future NGA
  // storefront whose hostname contains "nga" resolves to NGA.
  function store() {
    var h = ''
    try { h = (window.location.hostname || '').toLowerCase() } catch (e) {}
    if (h.indexOf('nga') !== -1) return 'NGA'
    return 'INT'
  }

  // ── product handle from /products/<handle> ────────────────────────────────────
  function productHandle() {
    try {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/)
      return m ? m[1] : null
    } catch (e) { return null }
  }

  function safe(fn) { try { return fn() } catch (e) { return null } }

  // ── cart stitching ──────────────────────────────────────────────────────────
  // Persist visitor/session id onto the Shopify cart as attributes so they carry
  // through to the order and are readable from the checkout_completed web pixel
  // via checkout.attributes. Cross-origin localStorage is NOT shared with the
  // checkout origin, so the cart is the reliable bridge. Guarded to one write
  // per session to avoid spamming /cart/update.js on every page view.
  function stampCart() {
    try {
      if (ssGet('rv_cart_stamped') === '1') return
      var root = '/'
      try { if (window.Shopify && Shopify.routes && Shopify.routes.root) root = Shopify.routes.root } catch (e) {}
      fetch(root + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          attributes: {
            rv_visitor_id: visitorId(),
            rv_session_id: sessionId()
          }
        })
      }).then(function () { ssSet('rv_cart_stamped', '1') })['catch'](function () {})
    } catch (e) {}
  }

  // ── send ──────────────────────────────────────────────────────────────────────
  function sendEvent(eventType, extra) {
    try {
      var u = utms()
      var f = fbData()
      var payload = {
        visitor_id: visitorId(),
        session_id: sessionId(),
        event_type: eventType,
        page_url: safe(function () { return window.location.href }),
        page_title: safe(function () { return document.title }),
        referrer: safe(function () { return document.referrer || null }),
        utm_source: u.utm_source || null,
        utm_medium: u.utm_medium || null,
        utm_campaign: u.utm_campaign || null,
        utm_content: u.utm_content || null,
        utm_term: u.utm_term || null,
        fbclid: f.fbclid || null,
        ad_id: f.ad_id || null,
        campaign_id: f.campaign_id || null,
        product_handle: null,
        product_title: null,
        customer_email: null,
        order_id: null,
        store: store()
      }
      if (extra) {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]
        }
      }

      var body = JSON.stringify(payload)
      // text/plain keeps this a CORS "simple request" (no preflight) for both transports.
      var sent = false
      try {
        if (navigator.sendBeacon) {
          sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
        }
      } catch (e) { sent = false }
      if (!sent) {
        try {
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: body,
            keepalive: true,
            mode: 'cors'
          })['catch'](function () {})
        } catch (e) {}
      }
    } catch (e) { /* never throw — the storefront must be unaffected */ }
  }

  // ── public API ──────────────────────────────────────────────────────────────
  // Called from the theme (add-to-cart) and the order confirmation page (purchase).
  window.RivanyPixel = {
    addToCart: function (productHandleArg, productTitleArg) {
      // Stamp the cart at add-to-cart time too, so the id is on the cart even if
      // the page-load stamp was skipped or hadn't completed yet.
      stampCart()
      sendEvent('add_to_cart', {
        product_handle: productHandleArg || productHandle(),
        product_title: productTitleArg || null
      })
    },
    purchase: function (orderId, customerEmail) {
      sendEvent('purchase', {
        order_id: orderId != null && orderId !== '' ? String(orderId) : null,
        customer_email: customerEmail || null
      })
    }
  }

  // ── auto-fire on load (deferred so it never blocks render) ────────────────────
  function boot() {
    sendEvent('page_view', {})
    stampCart()
    var ph = productHandle()
    if (ph) {
      sendEvent('product_view', {
        product_handle: ph,
        product_title: safe(function () { return document.title })
      })
    }
  }
  try {
    if (window.requestIdleCallback) requestIdleCallback(boot)
    else setTimeout(boot, 0)
  } catch (e) {
    try { setTimeout(boot, 0) } catch (e2) {}
  }
})()
